// React imports
import { lazy, Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Third-party library imports
import { Card, CardBody, Container, Row } from "reactstrap";

// Constants
import {
  AddReimbursementForm,
  DownloadReimbursementForm,
  excel,
  ReimbursementModal,
  ReimbursementModalBills,
  ReimbursementUserDetailsModal,
  img,
  pdf,
  reimbursementDeleteDialog,
  submit,
  success,
} from "../../../utils/Constant";

//Service
import IconComponent from "../../../CommonElements/IconComponent";
import FinancialYearCycle from "../../../CommonElements/YearPickerCycle";
import { setSelectedFinancialYear } from "../../../ReduxToolkit/Slice/FinancialYearSlice";

import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import { Nullable } from "../../../Types/Common";

import {
  fetchEmployeesListThunk,
} from "../../hrZone/Users/redux/slice/EmployeesListSlie";
import {
  closeModal,
  openModal,
} from "../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { Btn } from "../../../AbstractElements";
import Loader from "../../../Layout/Loader/Loader";
import ReimbursementTable from "./table/ReimbursementTable";
import ReimbursementCards from "./cards/ReimbursementCards";
import { handleDownload } from "../../../utils/fileUtils";
import { useModalSelector } from "../../../hooks/useModalSelector";
import { ExtensionTypes, IReimbursementServiceList } from "../types";
import { selectEmployeeSecondaryTabActive } from "../../employeeManagement/redux/selector/EmployeeManagementSelector";
import { useEmployeeProfile } from "../../../hooks/useEmployeeProfile";
import {
  useDeleteReimbursementDetailMutation,
  useReimbursementCategoryListQuery,
  useReimbursementListQuery,
} from "../../../ReduxToolkit/api/reimbursement";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  setResetFunctions,
  showEmployeeNotificationForm,
} from "../../employeeManagement/redux/slice/EmployeeManagementSlice";
import { logErrors } from "../../../utils/logErrors";
import { showApiResultAlert } from "../../../utils/AlertManager/showApiAlert";

const ReimbursementModalForm = lazy(
  () => import("./modal/ReimbursementFormModal")
);
const ConfirmDeleteDialog = lazy(
  () => import("../../../CommonElements/ConfirmDeleteDialog")
);

const ReimbursementUserDetailModal = lazy(
  () => import("./modal/ReimbursementDetailsModal")
);

const ReimbursementModalBill = lazy(() => import("./modal/BillModal"));

const ReimbursementContainer = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();
  const [billCollection, setBillCollection] = useState<ExtensionTypes>({
    img: [],
    pdf: [],
    excel: [],
  });
  const [reimbursementUserDetails, setReimbursementUserDetails] =
    useState<Nullable<IReimbursementServiceList>>(null);

  const secondaryTabActive = useAppSelector(selectEmployeeSecondaryTabActive);

  const { isOpen, modalType, selectedId } = useModalSelector();

  const selectedFinancialYear = useAppSelector(
    ({ financialYearSlice }) =>
      financialYearSlice?.reimbursement.selectedFinancialYear
  );

  const financialYear = selectedFinancialYear?.split("-")[0];

  const employeeId = Number(employeeProfileDetails?.jobInformation?.userId);

  const shouldFetchGlobal =
    (location.pathname === "/reimbursement" && financialYear && employeeId) ||
    (employeeId && Number(secondaryTabActive) === 302 && financialYear);

  const {
    data: reimbursementList,
    isLoading: isReimbursementListLoading,
    isError: isReimbursementListError,
    error: reimbursementListError,
  } = useReimbursementListQuery(shouldFetchGlobal ? employeeId : skipToken);

  const {
    data: reimbursementCategoriesList,
    isError: isReimbursementCategoryListError,
    error: reimbursementCategoryListError,
  } = useReimbursementCategoryListQuery(financialYear ? undefined : skipToken);

  const [
    deleteReimbursementInfo,
    { isLoading, reset: resetDeleteReimbursementInfo },
  ] = useDeleteReimbursementDetailMutation();

  const extensionsMap: Record<string, keyof typeof categorizedFiles> = {
    jpeg: img,
    jpg: img,
    png: img,
    webp: img,
    gif: img,
    svg: img,
    pdf: pdf,
    xls: excel,
    xlsx: excel,
    xlsm: excel,
    xlsb: excel,
    csv: excel,
  };

  const categorizedFiles = {
    img: [] as string[],
    pdf: [] as string[],
    excel: [] as string[],
  };

  const findUserBill = (billId: number) => {
    const bill = reimbursementList?.data?.find(
      ({ id }) => Number(id) === Number(billId)
    )?.bill;
    if (bill) {
      bill?.split(",").forEach((file: string) => {
        const extension = file.split(".")?.pop()?.toLowerCase();
        const category = extension && extensionsMap[extension];

        if (category) {
          categorizedFiles[category].push(file);
        }
      });

      setBillCollection(categorizedFiles);
    }
  };

  const handleOpenDialog = (reImbursementId: number) => {
    dispatch(
      openModal({
        modalType: reimbursementDeleteDialog,
        selectedId: reImbursementId,
      })
    );
  };

  const handleAddReimbursementForm = () =>
    dispatch(openModal({ modalType: ReimbursementModal }));

  const handleCloseModal = () => {
    if (modalType !== ReimbursementModal) {
      setBillCollection({ img: [], pdf: [], excel: [] });
    }
    dispatch(closeModal());
  };

  const handleSetSelectedYear = (year: string) => {
    dispatch(setSelectedFinancialYear({ module: "reimbursement", year }));
  };

  const handleDeleteReimbursementItem = async () => {
    if (!selectedId) return logErrors("Delete reimbursement id is missing");

    const deleteReimbursementInfoResponse = await deleteReimbursementInfo({
      id: Number(selectedId),
    });
    dispatch(
      setResetFunctions({
        mutationReset: resetDeleteReimbursementInfo,
      })
    );
    dispatch(
      showEmployeeNotificationForm({
        message:
          deleteReimbursementInfoResponse?.data?.message ??
          deleteReimbursementInfoResponse?.error?.message ??
          "",
        type: deleteReimbursementInfoResponse?.data?.success
          ? "success"
          : "warning",
        title: `${employeeProfileDetails?.jobInformation?.name} Reimbursement Info`,
        source: reimbursementDeleteDialog,
      })
    );
    dispatch(closeModal());
  };

  useEffect(() => {
    dispatch(fetchEmployeesListThunk({ manager: false, status: 1 }));
  }, []);

  useEffect(() => {
    const shouldRun =
      isReimbursementListError || isReimbursementCategoryListError;
    const errors = reimbursementListError || reimbursementCategoryListError;

    if (shouldRun) {
      showApiResultAlert(errors, { title: `Hey ${loggedInEmployee?.name}!` });
    }
  }, [isReimbursementListError, isReimbursementCategoryListError]);

  return (
    <Container fluid className="default-dashboard">
      <Card>
        <CardBody>
          <div className="d-md-flex align-items-center justify-content-between">
            <div className="d-flex justify-content-between align-items-center mb-3 mb-lg-0">
              {Number(loggedInEmployee?.id) === Number(employeeId) && (
                <Btn
                  type={submit}
                  color={success}
                  className="f-12"
                  onClick={handleAddReimbursementForm}
                >
                  {AddReimbursementForm}
                </Btn>
              )}
              <div className="d-block d-md-none">
                <FinancialYearCycle
                  selectedYear={selectedFinancialYear ?? ""}
                  setSelectedYear={handleSetSelectedYear}
                  startMonth={9} // October (0-based index)
                  startDateValue={1}
                  endMonth={8} // September (0-based index)
                  endDateValue={30}
                />
              </div>
            </div>
            <div className="d-md-flex align-items-center">
              {Number(loggedInEmployee?.id) === Number(employeeId) && (
                <Link
                  to={`https://psislms.s3.ap-south-1.amazonaws.com/profile_pics/Outstation reimbursement form-1 (1).xlsx_1743511560.xlsx`}
                  target="_blank"
                >
                  <IconComponent className="fa fa-download text-primary f-16" />
                  <span className="text-primary ms-2 text-underline">
                    {DownloadReimbursementForm}
                  </span>
                </Link>
              )}
              <div className="ms-md-3 d-none d-md-block">
                <FinancialYearCycle
                  selectedYear={selectedFinancialYear ?? ""}
                  setSelectedYear={handleSetSelectedYear}
                  startMonth={9} // October (0-based index)
                  startDateValue={1}
                  endMonth={8} // September (0-based index)
                  endDateValue={30}
                />
              </div>
            </div>
          </div>

          <Row>
            <ReimbursementTable
              findUserBill={findUserBill}
              openDialog={handleOpenDialog}
              reimbursementList={reimbursementList?.data ?? []}
              reimbursementListLoading={isReimbursementListLoading}
              setReimbursementUserDetails={setReimbursementUserDetails}
            />
          </Row>
        </CardBody>
      </Card>
      <div className="d-block d-lg-none mb-4">
        <ReimbursementCards
          findUserBill={findUserBill}
          reimbursementList={reimbursementList?.data ?? []}
        />
      </div>
      <Suspense fallback={<Loader />}>
        {isOpen && modalType === ReimbursementModal && (
          <ReimbursementModalForm
            isOpen={isOpen}
            handleCloseModal={handleCloseModal}
            reimbursementCategoriesList={
              reimbursementCategoriesList?.data ?? []
            }
          />
        )}
      </Suspense>
      <Suspense fallback={<Loader />}>
        {isOpen &&
          modalType === ReimbursementUserDetailsModal &&
          reimbursementUserDetails && (
            <ReimbursementUserDetailModal
              isOpen={isOpen}
              handleCloseModal={handleCloseModal}
              details={reimbursementUserDetails}
            />
          )}
      </Suspense>
      <Suspense fallback={<Loader />}>
        {isOpen && modalType === ReimbursementModalBills && billCollection && (
          <ReimbursementModalBill
            isOpen={isOpen}
            handleCloseModal={handleCloseModal}
            collection={billCollection}
            handleDownloadCollection={handleDownload}
          />
        )}
      </Suspense>
      <Suspense fallback={<Loader />}>
        {isOpen && modalType === reimbursementDeleteDialog && (
          <ConfirmDeleteDialog
            isOpen={isOpen}
            size="md"
            label="Are you sure want to remove ?"
            handleToggle={() => dispatch(closeModal())}
            handleClickFirstBtn={handleDeleteReimbursementItem}
          />
        )}
      </Suspense>
    </Container>
  );
};

export default ReimbursementContainer;
